import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  User,
  Mail,
  Lock,
  Gift,
  TrendingUp,
  LogOut,
  LayoutDashboard,
  LineChart,
  Package,
  Settings,
  Menu,
  X,
  Copy,
  Check,
  Shield,
  Calendar,
  DollarSign,
  Users,
  ArrowLeft,
  Save,
  Eye,
  EyeOff,
} from "lucide-react";

const ProfilePage = () => {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  
  // Profile form state
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  
  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Referral stats
  const [referralStats, setReferralStats] = useState(null);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
    }
    fetchReferralStats();
  }, [user]);

  const fetchReferralStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/referral/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setReferralStats(res.data);
    } catch (error) {
      console.error("Failed to fetch referral stats:", error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleUpdateProfile = async () => {
    if (!name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/user/profile`,
        { name },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success("Profile updated successfully");
      checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/user/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success("Password changed successfully");
      setPasswordDialogOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (referralStats?.referral_code) {
      navigator.clipboard.writeText(referralStats.referral_code);
      setCopiedCode(true);
      toast.success("Referral code copied!");
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const copyReferralLink = () => {
    if (referralStats?.referral_code) {
      const link = `${window.location.origin}/register?ref=${referralStats.referral_code}`;
      navigator.clipboard.writeText(link);
      toast.success("Referral link copied!");
    }
  };

  const navItems = [
    { icon: <LayoutDashboard className="w-5 h-5" />, label: "Dashboard", href: "/dashboard" },
    { icon: <LineChart className="w-5 h-5" />, label: "Trading", href: "/trading" },
    { icon: <Package className="w-5 h-5" />, label: "Packages", href: "/packages" },
    { icon: <User className="w-5 h-5" />, label: "Profile", href: "/profile", active: true },
  ];

  if (user?.role === "admin") {
    navItems.push({ icon: <Settings className="w-5 h-5" />, label: "Admin Panel", href: "/admin" });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 sidebar-premium z-50 transform transition-transform duration-300 lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}>
        <div className="p-6 border-b border-slate-100">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading text-lg font-bold text-navy-700">AndInvestTrading</span>
          </Link>
        </div>

        <nav className="p-4 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                item.active
                  ? "bg-navy-600 text-white shadow-lg shadow-navy-600/20"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {item.icon}
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100">
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-slate-600 hover:text-rose-600 hover:bg-rose-50"
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200 sticky top-0 z-30">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <div>
                <h1 className="text-xl font-heading font-semibold text-navy-700">Profile Settings</h1>
                <p className="text-sm text-slate-500">Manage your account and preferences</p>
              </div>
            </div>
            <Link to="/dashboard">
              <Button variant="outline" className="gap-2 border-navy-200 text-navy-600">
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Profile Information */}
            <Card className="bg-white shadow-lg border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-navy-700 font-heading flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Profile Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex items-start gap-6">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <div className="w-24 h-24 bg-gradient-to-br from-navy-500 to-navy-700 rounded-2xl flex items-center justify-center shadow-lg">
                      <span className="text-4xl font-bold text-white">
                        {user?.name?.charAt(0)?.toUpperCase() || "U"}
                      </span>
                    </div>
                  </div>

                  {/* Form */}
                  <div className="flex-1 space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-700">Full Name</Label>
                        <Input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="border-slate-300"
                          data-testid="profile-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-700">Email Address</Label>
                        <Input
                          value={email}
                          disabled
                          className="border-slate-300 bg-slate-50"
                        />
                        <p className="text-xs text-slate-500">Email cannot be changed</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 pt-2">
                      <Button 
                        onClick={handleUpdateProfile} 
                        className="btn-gold gap-2"
                        disabled={loading}
                        data-testid="save-profile-btn"
                      >
                        <Save className="w-4 h-4" />
                        Save Changes
                      </Button>
                      
                      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="gap-2 border-slate-300">
                            <Lock className="w-4 h-4" />
                            Change Password
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-white border-slate-200">
                          <DialogHeader>
                            <DialogTitle className="text-navy-700 font-heading">Change Password</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 pt-4">
                            <div className="space-y-2">
                              <Label className="text-slate-700">Current Password</Label>
                              <div className="relative">
                                <Input
                                  type={showCurrentPassword ? "text" : "password"}
                                  value={currentPassword}
                                  onChange={(e) => setCurrentPassword(e.target.value)}
                                  className="border-slate-300 pr-10"
                                  data-testid="current-password-input"
                                />
                                <button
                                  type="button"
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                >
                                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-slate-700">New Password</Label>
                              <div className="relative">
                                <Input
                                  type={showNewPassword ? "text" : "password"}
                                  value={newPassword}
                                  onChange={(e) => setNewPassword(e.target.value)}
                                  className="border-slate-300 pr-10"
                                  data-testid="new-password-input"
                                />
                                <button
                                  type="button"
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                  onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-slate-700">Confirm New Password</Label>
                              <Input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="border-slate-300"
                                data-testid="confirm-password-input"
                              />
                            </div>
                            <Button 
                              onClick={handleChangePassword} 
                              className="w-full btn-gold"
                              disabled={loading}
                              data-testid="change-password-btn"
                            >
                              {loading ? "Changing..." : "Change Password"}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Details */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Account Info */}
              <Card className="bg-white shadow-lg border-slate-200">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-navy-700 font-heading flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Account Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-navy-100 rounded-lg flex items-center justify-center">
                        <Shield className="w-5 h-5 text-navy-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Account Type</p>
                        <p className="font-medium text-navy-700 capitalize">{user?.role || "User"}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Account Balance</p>
                        <p className="font-medium text-navy-700">${(user?.balance || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Member Since</p>
                        <p className="font-medium text-navy-700">
                          {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Referral Program */}
              <Card className="bg-white shadow-lg border-slate-200">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-navy-700 font-heading flex items-center gap-2">
                    <Gift className="w-5 h-5" />
                    Referral Program
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {/* Referral Code */}
                  <div className="bg-gradient-to-r from-amber-50 to-amber-100 p-4 rounded-xl border border-amber-200">
                    <p className="text-sm text-amber-700 mb-2">Your Referral Code</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xl font-mono font-bold text-amber-800 tracking-wider">
                        {referralStats?.referral_code || "------"}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-amber-700 hover:bg-amber-200"
                        onClick={copyReferralCode}
                        data-testid="copy-referral-code-btn"
                      >
                        {copiedCode ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                      </Button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 p-4 rounded-xl">
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4 text-navy-600" />
                        <p className="text-sm text-slate-500">Total Referrals</p>
                      </div>
                      <p className="text-2xl font-bold text-navy-700">{referralStats?.total_referrals || 0}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl">
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="w-4 h-4 text-emerald-600" />
                        <p className="text-sm text-slate-500">Bonus Earned</p>
                      </div>
                      <p className="text-2xl font-bold text-emerald-600">${user?.referral_balance || 0}</p>
                    </div>
                  </div>

                  {/* Copy Link Button */}
                  <Button
                    variant="outline"
                    className="w-full gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={copyReferralLink}
                    data-testid="copy-referral-link-btn"
                  >
                    <Copy className="w-4 h-4" />
                    Copy Referral Link
                  </Button>

                  <p className="text-xs text-slate-500 text-center">
                    Earn $100 for each friend who invests. Bonus is withdrawable after your first investment.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
